import React, { useEffect, useState, useRef, useContext } from "react";
import api from "../../utils/axiosInstance";
import { Link } from "react-router-dom";
import { User, LogOut, ChevronDown } from "lucide-react";
import LogoutConfirmModal from "../Modals/LogoutConfirmModal";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import BrandMark from "../Brand/BrandMark";

const Navbar = ({ sidebarOpen = false, logoHref = "/" }) => {
  const isExternalLogo = /^https?:\/\//.test(logoHref);
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user?.roleSlug !== "vendor") {
      setProfile(null);
      return;
    }
    api
      .get("/vendor-self")
      .then((res) => {
        setProfile(res.data?.data || res.data);
      })
      .catch(() => {
        setProfile(null);
      });
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleLogout = () => {
    setProfile(null);
    setShowLogoutConfirm(false);
    setOpen(false);
  };

  const handleMenuClick = (callback) => {
    setOpen(false);
    if (callback) callback();
  };

  const displayName =
    user?.full_name ||
    (user?.roleSlug === "vendor" ? profile?.company_name : null) ||
    user?.username ||
    user?.email ||
    "Account";

  const getInitials = () => {
    const fromName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((part) => part[0])
      .join("");
    return (fromName || displayName?.[0] || "U").toUpperCase();
  };

  // Avatar (fallback to initials if no image)
  const getAvatar = () => {
    if (user?.avatar_url) {
      return (
        <img
          src={user.avatar_url}
          alt={displayName}
          className="h-8 w-8 rounded-full border border-gray-200 object-cover"
        />
      );
    }
    return (
      <div className="grid h-8 w-8 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-white">
        {getInitials()}
      </div>
    );
  };


  const logo = <BrandMark compact={!sidebarOpen && Boolean(user)} />;

  return (
    <>
      <nav className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
        {isExternalLogo ? (
          <a
            href={logoHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center"
            aria-label="Globilise"
          >
            {logo}
          </a>
        ) : (
          <Link to={logoHref} className="group flex items-center" aria-label="Globilise">
            {logo}
          </Link>
        )}

        <div className="flex items-center gap-3">
          {/* Vendor context label */}
          {user?.roleSlug === "vendor" && profile?.type && (
            <div className="hidden items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500 sm:flex">
              <span className="font-semibold text-gray-800">{profile.type}</span>
              vendor of
              <span className="font-semibold text-gray-800">
                {profile.admin?.details
                  ? profile.admin.details.account_type === "company"
                    ? profile.admin.details.company_name
                    : `${profile.admin.details.first_name} ${profile.admin.details.last_name}`
                  : "—"}
              </span>
            </div>
          )}

          {!user ? (
            <>
              <Link
                to="/login"
                className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
              >
                Log In
              </Link>
              <Link
                to="/create-account"
                className="rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-card transition hover:bg-brand-700"
              >
                Create an Account
              </Link>
            </>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-2.5 transition hover:border-gray-300 hover:bg-gray-50"
              >
                {getAvatar()}
                <span className="hidden max-w-[10rem] truncate text-sm font-medium text-gray-700 sm:inline">
                  {displayName}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 mt-2 w-60 overflow-hidden rounded-2xl border border-gray-200 bg-white text-gray-800 shadow-pop"
                  >
                    <div className="border-b border-gray-100 px-4 py-3">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {displayName}
                      </p>
                      <p className="truncate text-xs text-gray-500">{user?.email || ""}</p>
                    </div>

                    <div className="p-1.5">
                      <Link
                        to="/profile"
                        onClick={() => handleMenuClick()}
                        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                      >
                        <User className="h-4 w-4 text-gray-400" />
                        My Profile
                      </Link>

                      <button
                        onClick={() => handleMenuClick(() => setShowLogoutConfirm(true))}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-gray-600 transition hover:bg-rose-50 hover:text-rose-600"
                      >
                        <LogOut className="h-4 w-4 text-gray-400" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </nav>

      {showLogoutConfirm && (
        <LogoutConfirmModal
          onCancel={() => setShowLogoutConfirm(false)}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};

export default Navbar;